import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  X,
  MoreVertical,
  Check,
  ArrowRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  Crown,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import { openPaytmCheckout } from "../lib/paytmService";

export default function YourSubscriptionModal({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [apiFeatures, setApiFeatures] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState("");
  const [otherCancelReason, setOtherCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlanId, setSelectedUpgradePlanId] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [toast, setToast] = useState(null);

  const menuRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Close 3-dots menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Load subscription and plans when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      loadSubscriptionData();
    } else {
      document.body.style.overflow = "auto";
      setShowMenu(false);
      setShowCancelModal(false);
      setShowUpgradeModal(false);
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const loadSubscriptionData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch current active subscription
      const subRes = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION);
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      } else {
        setSubscription(null);
      }

      // 2. Fetch available plans for upgrade comparison directly from API
      try {
        const plansRes = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION_PLANS);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setAvailablePlans(Array.isArray(plansData?.plans) ? plansData.plans : []);
          if (Array.isArray(plansData?.features)) {
            setApiFeatures(plansData.features);
          }
        } else {
          setAvailablePlans([]);
        }
      } catch (err) {
        console.warn("Could not load plans list from API:", err);
        setAvailablePlans([]);
      }
    } catch (err) {
      console.error("Error loading subscription:", err);
      setError("Couldn't load subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const reason = (
      selectedCancelReason === "Other" ? otherCancelReason : selectedCancelReason
    ).trim();

    if (!reason) {
      showToast("Please choose or enter a cancellation reason", "error");
      return;
    }

    setCancelling(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION_CANCEL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });

      if (res.ok) {
        showToast("Subscription cancelled successfully", "success");
        setShowCancelModal(false);
        loadSubscriptionData();
        if (onSuccess) onSuccess();
      } else {
        const errData = await res.json().catch(() => ({}));
        showToast(errData.message || "Failed to cancel subscription", "error");
      }
    } catch (err) {
      console.error("Cancel subscription error:", err);
      showToast("Network error cancelling subscription", "error");
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgradeSubscription = async () => {
    if (!selectedUpgradePlanId) {
      showToast("Please select a plan to upgrade", "error");
      return;
    }

    setUpgrading(true);
    try {
      const userPhone = localStorage.getItem("number") || "";
      const isUpgrading = Boolean(subscription?.status === "active" || subscription?.id);
      const endpoint = isUpgrading
        ? API_ENDPOINTS.PAYTM_SUBSCRIPTION_UPGRADE
        : API_ENDPOINTS.PAYTM_SUBSCRIPTION;
      const method = isUpgrading ? "PUT" : "POST";
      const bodyPayload = isUpgrading
        ? {
            newPlanId: selectedUpgradePlanId.trim(),
            ...(userPhone ? { mobile: userPhone, phoneNumber: userPhone, phone: userPhone } : {}),
          }
        : {
            planId: selectedUpgradePlanId.trim(),
            ...(userPhone ? { mobile: userPhone, phoneNumber: userPhone, phone: userPhone } : {}),
          };

      console.log(`Upgrading subscription via ${method} ${endpoint} with:`, bodyPayload);

      const res = await fetchWithAuth(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const errMsg =
          errData.message ||
          errData.error ||
          `Backend returned ${res.status}: Failed to process plan upgrade.`;
        console.error("Paytm plan upgrade request failed:", res.status, errData);
        showToast(errMsg, "error");
        setUpgrading(false);
        return;
      }

      const data = await res.json();
      console.log("Paytm subscription upgrade response:", data);

      // If backend indicates a new mandate or payment is needed, launch Paytm CheckoutJS
      if (data.needsNewMandate === true || (!isUpgrading && data.txnToken)) {
        if (!data.id || !data.txnToken || !data.mid) {
          const errMsg = "Payment parameters missing from server response. Please retry.";
          showToast(errMsg, "error");
          setUpgrading(false);
          return;
        }

        const mandateAmount =
          data.currentAmount ||
          data.mandateMaxAmount ||
          50000;

        await openPaytmCheckout({
          orderId: data.id,
          txnToken: data.txnToken,
          amount: mandateAmount,
          mid: data.mid,
          callbackUrl: data.callbackUrl,
          mobileNumber: userPhone,
          onSuccess: async () => {
            console.log("Paytm upgrade checkout success, verifying mandate orderId:", data.id);
            try {
              const verifyRes = await fetchWithAuth(
                API_ENDPOINTS.PAYTM_SUBSCRIPTION_VERIFY,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ orderId: data.id }),
                }
              );

              if (verifyRes.ok) {
                showToast(
                  isUpgrading
                    ? "Plan upgraded successfully!"
                    : "Subscription activated successfully!",
                  "success"
                );
                setShowUpgradeModal(false);
                loadSubscriptionData();
                if (onSuccess) onSuccess();
              } else {
                const verifyErrData = await verifyRes.json().catch(() => ({}));
                const verifyErrMsg =
                  verifyErrData.message ||
                  "Payment completed, but verification failed on server.";
                showToast(verifyErrMsg, "error");
              }
            } catch (vErr) {
              console.error("Verification error during upgrade:", vErr);
              showToast("Verification request failed. Please check status.", "error");
            } finally {
              setUpgrading(false);
            }
          },
          onError: (err) => {
            console.error("Paytm transaction error during upgrade:", err);
            const errMsg =
              err?.message || "Payment cancelled or failed. Please try again.";
            showToast(errMsg, "error");
            setUpgrading(false);
          },
          onClose: () => {
            console.log("Paytm checkout popup closed");
            setUpgrading(false);
          },
        });
      } else {
        // Upgrade applied instantly within existing mandate
        showToast("Plan upgraded successfully!", "success");
        setShowUpgradeModal(false);
        loadSubscriptionData();
        if (onSuccess) onSuccess();
        setUpgrading(false);
      }
    } catch (err) {
      console.error("Upgrade error:", err);
      showToast(err?.message || "Error processing upgrade", "error");
      setUpgrading(false);
    }
  };

  // Format Helpers
  const formatMemberTill = (isoDate) => {
    if (!isoDate) return "Sep 2026";
    try {
      const d = new Date(isoDate);
      return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch {
      return "Active";
    }
  };

  const formatAmount = (paiseOrRupees) => {
    if (paiseOrRupees == null) return "0";
    // If backend returns paise, convert if >= 100
    const amt = typeof paiseOrRupees === "number" ? paiseOrRupees : parseInt(paiseOrRupees, 10);
    return amt.toLocaleString("en-IN");
  };

  const isAnnual =
    subscription?.billingPeriod?.toLowerCase() === "annual" ||
    subscription?.frequencyUnit?.toUpperCase() === "YEAR" ||
    subscription?.name?.toLowerCase().includes("annual") ||
    subscription?.type?.toLowerCase().includes("annual");

  const displayPlanName =
    subscription?.type ||
    subscription?.planType ||
    subscription?.name ||
    "Subscription";

  const rawAmount =
    subscription?.currentAmount ?? subscription?.mandateMaxAmount ?? 0;
  // If amount is in paise (> 100), divide by 100, otherwise display as is
  const displayRupees =
    rawAmount >= 100 ? Math.round(rawAmount / 100) : rawAmount;

  const status = (subscription?.status || "active").toLowerCase();
  const isActive =
    status === "active" || status === "subscribed" || status === "authenticated";

  // Usage info
  const usage = subscription?.usage;
  const maxEarnings = usage?.maxEarnings ?? subscription?.maxEarnings ?? null;
  const remainingEarnings = usage?.remainingEarnings ?? maxEarnings;
  const earnedAmount = usage?.earnings ?? 0;

  const maxRequests = usage?.maxRequests ?? subscription?.maxRequests ?? null;
  const remainingRequests = usage?.remainingRequests ?? maxRequests;
  const requestsUsed = usage?.requests ?? 0;

  // Features list strictly from subscription or API response (no hardcoded fallbacks)
  const featuresList =
    Array.isArray(subscription?.features) && subscription.features.length > 0
      ? subscription.features
      : apiFeatures;

  // Filter plans to only show upgradeable candidates (higher tier / price)
  const currentAmount =
    subscription?.currentAmount ?? subscription?.mandateMaxAmount ?? 0;
  const currentPlanId = subscription?.planId;

  const upgradeablePlans = availablePlans
    .map((plan) => ({
      ...plan,
      interval: isAnnual ? plan.annual : plan.quarterly,
    }))
    .filter((p) => {
      if (!p.interval || !p.interval.id) return false;
      if (currentPlanId && p.interval.id === currentPlanId) return false;
      if (isActive && currentAmount > 0 && p.interval.amount != null) {
        return p.interval.amount > currentAmount;
      }
      return true;
    });

  // Auto-select first available upgradeable plan
  useEffect(() => {
    if (showUpgradeModal && upgradeablePlans.length > 0) {
      if (
        !selectedUpgradePlanId ||
        !upgradeablePlans.some((p) => p.interval?.id === selectedUpgradePlanId)
      ) {
        setSelectedUpgradePlanId(upgradeablePlans[0].interval.id);
      }
    }
  }, [showUpgradeModal, upgradeablePlans, selectedUpgradePlanId]);

  const cancellationReasons = [
    "Too expensive",
    "Not using the features",
    "Found a better alternative",
    "Technical issues",
    "Poor customer service",
    "No longer need the service",
    "Temporary financial constraints",
    "Switching to free version",
    "Other",
  ];

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 animate-fadeIn">
      {/* Toast Alert */}
      {toast && (
        <div
          className={`fixed top-5 left-1/2 -translate-x-1/2 z-[150] px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium flex items-center gap-2 transition-all ${
            toast.type === "error" ? "bg-red-600" : "bg-emerald-600"
          }`}
        >
          {toast.type === "error" ? (
            <AlertCircle className="w-4 h-4" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Main Card Container */}
      <div className="bg-[#F8F9FB] rounded-3xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden relative border border-gray-100 animate-slideUp">
        {/* Top App Bar */}
        <div className="flex items-center justify-between px-4 py-3.5 bg-white border-b border-gray-100 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center text-gray-700"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900 font-sans tracking-tight">
              Your Subscription
            </h2>
          </div>

          {/* 3-dots Menu for Cancel Subscription */}
          {isActive && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center text-purple-700"
                aria-label="Options menu"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-10 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-20 animate-fadeIn">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      setShowCancelModal(true);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium transition-colors"
                  >
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {loading ? (
            <div className="py-24 flex flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-[#6720A4]" />
              <p className="text-sm font-medium">Loading your subscription...</p>
            </div>
          ) : error ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-center px-4">
              <AlertCircle className="w-10 h-10 text-red-500" />
              <p className="text-sm font-medium text-gray-700">{error}</p>
              <button
                onClick={loadSubscriptionData}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#6720A4] text-white text-sm font-semibold hover:bg-[#551887] transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          ) : !isActive && !subscription ? (
            /* No Active Subscription Fallback */
            <div className="space-y-4 py-2">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #6720A4 0%, #853068 100%)",
                }}
                className="rounded-2xl p-6 text-white text-center shadow-lg"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-3">
                  <Crown className="w-7 h-7 text-amber-300" />
                </div>
                <h3 className="text-xl font-bold mb-1">No Active Membership</h3>
                <p className="text-xs text-white/80 max-w-xs mx-auto mb-4">
                  Zero commission on fan requests & tips. Unlock your complete
                  potential.
                </p>
                <div className="border-t border-white/20 pt-4 space-y-2 text-left text-xs text-white/90">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    <span>Zero commission on fan song requests & tips</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    <span>Host live events & venue requests</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-300" />
                    <span>Direct payouts straight to your bank</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full bg-[#6720A4] hover:bg-[#551887] text-white font-bold py-3.5 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-transform active:scale-[0.98]"
              >
                <span>Get Membership</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Active Subscription View - Matches Screenshot */
            <>
              {/* Premium Gradient Hero Card */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #581694 0%, #6F1E85 45%, #883163 100%)",
                }}
                className="rounded-2xl p-5 sm:p-6 text-white shadow-xl relative overflow-hidden"
              >
                {/* Top Row: Status Chip & Watermark Icon */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-950/40 text-emerald-400 border border-emerald-500/20 text-[11px] font-bold tracking-wide">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    ACTIVE
                  </span>

                  {/* Top Right Watermark Icon */}
                  <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center opacity-60">
                    <RefreshCw className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Plan Name & Interval */}
                <div className="mt-3">
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    {displayPlanName}
                  </h1>
                  <p className="text-xs sm:text-sm text-white/80 font-medium mt-0.5">
                    {isAnnual ? "Annual Plan" : "Quarterly Plan"}
                  </p>
                </div>

                {/* Subtle Divider */}
                <div className="border-t border-white/20 my-4" />

                {/* Amount Paid & Member Till */}
                <div className="flex items-end justify-between">
                  <div>
                    <span className="block text-[10px] font-bold text-white/60 tracking-wider uppercase mb-1">
                      AMOUNT PAID
                    </span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-extrabold text-white">
                        ₹{formatAmount(displayRupees)}
                      </span>
                      <span className="text-xs text-white/80 font-medium">
                        {isAnnual ? "/year" : "/3 months"}
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="block text-[10px] font-bold text-white/60 tracking-wider uppercase mb-1">
                      MEMBER TILL
                    </span>
                    <span className="text-sm font-bold text-white">
                      {formatMemberTill(
                        subscription?.expiryDate ||
                          subscription?.startDate ||
                          subscription?.createdAt
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* REMAINING BALANCE & USAGE SECTION */}
              <div className="space-y-3">
                <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  REMAINING BALANCE &amp; USAGE
                </h3>

                {/* Remaining Earnings Limit Card */}
                <div className="bg-white rounded-2xl p-4 border border-gray-150 shadow-sm space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">💰</span>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">
                        Remaining Earnings Limit
                      </span>
                    </div>
                    <span className="text-xs font-bold text-[#6720A4] text-right">
                      {maxEarnings != null
                        ? `₹${formatAmount(remainingEarnings)} / ₹${formatAmount(maxEarnings)}`
                        : "Unlimited Earning Potential"}
                    </span>
                  </div>

                  {/* Progress Line */}
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-[#6720A4] h-2 rounded-full transition-all duration-500"
                      style={{
                        width:
                          maxEarnings && maxEarnings > 0
                            ? `${Math.max(
                                5,
                                Math.min(
                                  100,
                                  Math.round((remainingEarnings / maxEarnings) * 100)
                                )
                              )}%`
                            : "100%",
                      }}
                    />
                  </div>

                  <p className="text-[11px] text-gray-400 font-medium">
                    {maxEarnings != null
                      ? `₹${formatAmount(earnedAmount)} earned in current cycle`
                      : "No capping limit on earnings"}
                  </p>
                </div>

                {/* Remaining Fan Requests Card */}
                <div className="bg-white rounded-2xl p-4 border border-gray-150 shadow-sm space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">🎶</span>
                      <span className="text-xs sm:text-sm font-bold text-gray-900">
                        Remaining Fan Requests
                      </span>
                    </div>
                    <span className="text-xs font-bold text-[#6720A4] text-right">
                      {maxRequests != null
                        ? `${remainingRequests} / ${maxRequests}`
                        : "Unlimited Requests"}
                    </span>
                  </div>

                  {/* Progress Line */}
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-[#6720A4] h-2 rounded-full transition-all duration-500"
                      style={{
                        width:
                          maxRequests && maxRequests > 0
                            ? `${Math.max(
                                5,
                                Math.min(
                                  100,
                                  Math.round((remainingRequests / maxRequests) * 100)
                                )
                              )}%`
                            : "100%",
                      }}
                    />
                  </div>

                  <p className="text-[11px] text-gray-400 font-medium">
                    {maxRequests != null
                      ? `${requestsUsed} requests processed in current cycle`
                      : "No limit on fan requests"}
                  </p>
                </div>
              </div>

              {/* WHAT'S INCLUDED SECTION */}
              <div className="space-y-2.5 pb-2">
                <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  WHAT&apos;S INCLUDED
                </h3>

                <div className="space-y-2">
                  {featuresList.map((feature, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-xl px-4 py-3 border border-gray-150 shadow-xs flex items-center gap-3"
                    >
                      <div className="w-5 h-5 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-[#6720A4] stroke-[2.5]" />
                      </div>
                      <span className="text-xs sm:text-[13px] font-semibold text-gray-800 leading-snug">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Upgrade Plan Button (Sticky at Bottom) */}
        {isActive && (
          <div className="p-4 bg-white/95 backdrop-blur-md border-t border-gray-100">
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="w-full bg-[#6720A4] hover:bg-[#57198C] active:scale-[0.98] text-white font-bold py-3.5 px-5 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-300/40 transition-all"
            >
              <span>Upgrade Plan</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Cancellation Submodal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 space-y-4 border border-gray-100 animate-slideUp">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">
                Cancel Subscription
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="w-7 h-7 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-gray-500 leading-relaxed">
              We&apos;re sorry to see you go! Please let us know why you are
              cancelling:
            </p>

            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {cancellationReasons.map((reason) => (
                <label
                  key={reason}
                  className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-purple-50/50 cursor-pointer text-xs font-medium text-gray-700"
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={selectedCancelReason === reason}
                    onChange={(e) => setSelectedCancelReason(e.target.value)}
                    className="accent-[#6720A4] w-3.5 h-3.5"
                  />
                  <span>{reason}</span>
                </label>
              ))}
            </div>

            {selectedCancelReason === "Other" && (
              <textarea
                rows={2}
                value={otherCancelReason}
                onChange={(e) => setOtherCancelReason(e.target.value)}
                placeholder="Please describe reason..."
                className="w-full text-xs p-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#6720A4]"
              />
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Keep Plan
              </button>
              <button
                type="button"
                disabled={cancelling}
                onClick={handleCancelSubscription}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all disabled:opacity-50"
              >
                {cancelling ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Submodal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-5 space-y-4 border border-gray-100 animate-slideUp">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Upgrade Your Plan
                </h3>
                <p className="text-[11px] text-gray-500">
                  Unlock higher earnings and fan request capacity
                </p>
              </div>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-7 h-7 rounded-full hover:bg-gray-100 text-gray-500 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {upgradeablePlans && upgradeablePlans.length > 0 ? (
                upgradeablePlans.map((plan, idx) => {
                  const planInterval = plan.interval;
                  const planId = planInterval?.id;
                  const price = (planInterval?.amount || 0) / 100;
                  const isSelected = selectedUpgradePlanId === planId;

                  return (
                    <div
                      key={planId || idx}
                      onClick={() => setSelectedUpgradePlanId(planId)}
                      className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                        isSelected
                          ? "border-[#6720A4] bg-purple-50/40 shadow-xs"
                          : "border-gray-150 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div>
                        <span className="font-bold text-xs sm:text-sm text-gray-900 block">
                          {plan.type || planInterval?.name || `Plan ${idx + 1}`}
                        </span>
                        <span className="text-[11px] text-gray-500">
                          {isAnnual ? "Annual Billing" : "Quarterly Billing"}
                        </span>
                        {planInterval?.maxEarnings && (
                          <span className="text-[10px] text-purple-700 font-medium block">
                            Earn up to ₹{planInterval.maxEarnings.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-extrabold text-sm text-[#6720A4]">
                          ₹{formatAmount(price)}
                        </span>
                        <span className="text-[10px] text-gray-400 block">
                          {isAnnual ? "/year" : "/quarter"}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 rounded-2xl bg-purple-50 text-center text-xs text-purple-900 border border-purple-100">
                  You are already on the highest available plan for this billing cycle.
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="button"
                disabled={upgrading || !selectedUpgradePlanId || upgradeablePlans.length === 0}
                onClick={handleUpgradeSubscription}
                className="w-full py-3 rounded-xl bg-[#6720A4] hover:bg-[#551887] text-white text-xs font-bold transition-all shadow-md shadow-purple-200 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {upgrading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Upgrading...</span>
                  </>
                ) : (
                  <span>Confirm Upgrade</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
